import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, Settings, LogOut, Coins, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LanguageToggle } from '@/components/LanguageToggle';

export function TopBar({ title, onMenuToggle }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header
      data-testid="topbar"
      className="h-14 sm:h-16 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]/80 backdrop-blur-xl flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30"
    >
      <div className="flex items-center gap-3">
        {/* Mobile hamburger */}
        <button
          className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-white/5 transition-colors"
          onClick={onMenuToggle}
          data-testid="topbar-menu-toggle"
        >
          <Menu className="w-5 h-5" />
        </button>
        <h1 className="text-base sm:text-lg font-semibold font-['Space_Grotesk'] tracking-tight truncate">{title}</h1>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        <div className="hidden sm:block">
          <LanguageToggle />
        </div>

        {/* SafeCoin Balance */}
        <button
          data-testid="topbar-safecoin-balance"
          onClick={() => navigate('/safecoin')}
          className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 hover:bg-yellow-500/20 transition-colors cursor-pointer"
        >
          <Coins className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-500" />
          <span className="text-xs sm:text-sm font-semibold text-yellow-500">{user?.safecoin_balance ?? 0}</span>
          <span className="text-[10px] sm:text-xs text-yellow-500/70 hidden sm:inline">SC</span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-200">
              <Avatar className="h-7 w-7 sm:h-8 sm:w-8 border border-[hsl(var(--border))]">
                <AvatarFallback className="bg-[hsl(var(--secondary))] text-xs sm:text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium hidden md:inline">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
              <User className="w-4 h-4 mr-2" /> {t('nav.settings')}
            </DropdownMenuItem>
            {/* Mobile language toggle */}
            <div className="sm:hidden px-2 py-1.5">
              <LanguageToggle />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-[hsl(var(--destructive))]">
              <LogOut className="w-4 h-4 mr-2" /> {t('nav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
